import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { priceLabel, statusBadgeClass, statusBadgeLabel } from "@/lib/data";
import Lightbox from "@/components/Lightbox";
import { Phone, Mail, ArrowLeft, Check, Expand } from "lucide-react";

export default function ForkliftDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [img, setImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", suburb: "", message: "", interest: "More information" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get(`/forklifts/${slug}`).then(r => setF(r.data)).catch(() => nav("/forklifts-for-sale")); }, [slug, nav]);
  if (!f) return <div className="max-w-7xl mx-auto px-6 py-20 text-center uppercase tracking-widest text-sm text-[#666]">Loading…</div>;

  const gallery = f.gallery_images?.length ? f.gallery_images : [f.main_image].filter(Boolean);
  const isHire = f.listing_type === "hire" || f.listing_type === "sale_hire";
  const isSale = f.listing_type === "sale" || f.listing_type === "sale_hire";

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/enquiries", {
        kind: isSale ? "sale" : "hire", ...form,
        forklift_id: f.forklift_id, forklift_title: f.title,
        extra: { interest: form.interest, stock_number: f.stock_number || "" },
      });
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm({ name: "", company: "", phone: "", email: "", suburb: "", message: "", interest: "More information" });
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const specs = [
    ["Stock No.", f.stock_number], ["Brand", f.brand], ["Model", f.model], ["Year", f.year],
    ["Serial No.", f.serial_number], ["Hours", f.hours?.toLocaleString()],
    ["Capacity", f.capacity_kg && `${f.capacity_kg} kg`], ["Fuel / Power", f.fuel_type],
    ["Machine Type", f.machine_type], ["Mast Type", f.mast_type],
    ["Lift Height", f.lift_height_mm && `${f.lift_height_mm} mm`],
    ["Lowered Height", f.lowered_height_mm && `${f.lowered_height_mm} mm`],
    ["Fork Length", f.fork_length_mm && `${f.fork_length_mm} mm`],
    ["Transmission", f.transmission], ["Tyres", f.tyres],
    ["Attachments", f.attachments], ["Condition", f.condition], ["Location", f.location],
  ].filter(([, v]) => v);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <Link to={isSale ? "/forklifts-for-sale" : "/forklift-hire-brisbane"} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold mb-6 hover:text-[#D71920]" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Back to listings
      </Link>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <button type="button" onClick={() => setLightboxOpen(true)}
            className="aspect-[4/3] bg-[#f4f4f4] border border-[#e5e5e5] w-full relative group cursor-zoom-in overflow-hidden"
            aria-label="Open full-screen viewer" data-testid="main-image-trigger">
            {gallery[img] && <img src={gallery[img]} alt={f.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
            <div className="absolute top-3 right-3 bg-black/60 text-white px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold">
              <Expand className="w-3.5 h-3.5" /> View
            </div>
          </button>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {gallery.map((g, i) => (
                <button key={g} onClick={() => setImg(i)} className={`aspect-square border ${i === img ? "border-[#D71920] border-2" : "border-[#e5e5e5]"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className={statusBadgeClass(f.status)}>{statusBadgeLabel(f.status)}</span>
            {f.year && <span className="text-[#666] font-mono-tech text-sm">{f.year}</span>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-tight">{f.title}</h1>
          <div className="mt-3 text-[#555]">{f.brand} · {f.machine_type} · {f.fuel_type}</div>

          <div className="mt-6 border-t border-b border-[#e5e5e5] py-6">
            {isSale && (
              <div><div className="text-xs uppercase tracking-widest text-[#666]">Sale Price</div>
              <div className="font-display text-4xl text-[#D71920] font-bold">{f.sale_price ? `$${f.sale_price.toLocaleString()} + GST` : "Price on Application"}</div></div>
            )}
            {isHire && (
              <div className={isSale ? "mt-4" : ""}>
                <div className="text-xs uppercase tracking-widest text-[#666]">Rental</div>
                <div className="font-display text-3xl font-bold">{f.daily_hire_price ? `From $${f.daily_hire_price}/day` : "Request Rate"}</div>
                {(f.weekly_hire_price || f.monthly_hire_price) && (
                  <div className="text-sm text-[#555] mt-1">
                    {f.weekly_hire_price && <>Weekly ${f.weekly_hire_price}</>}
                    {f.weekly_hire_price && f.monthly_hire_price && " · "}
                    {f.monthly_hire_price && <>Monthly ${f.monthly_hire_price}</>}
                  </div>
                )}
              </div>
            )}
          </div>

          {f.description && <p className="mt-6 text-[#333] leading-relaxed">{f.description}</p>}
          {f.features && (
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {f.features.split(",").map(x => <li key={x} className="flex gap-2"><Check className="w-4 h-4 text-[#D71920] shrink-0 mt-0.5" />{x.trim()}</li>)}
            </ul>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+61482712785" className="ff-btn-dark" data-testid="detail-call"><Phone className="w-4 h-4" /> Call for Details</a>
            <a href="#enquire" className="ff-btn-primary" data-testid="detail-enquire"><Mail className="w-4 h-4" /> Enquire</a>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <h2 className="ff-accent-heading font-display text-3xl uppercase mb-5">Specifications</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
          {specs.map(([k, v]) => (
            <div key={k} className="bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#666]">{k}</div>
              <div className="font-display text-xl font-bold mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <form id="enquire" onSubmit={submit} className="mt-14 ff-card p-8" data-testid="enquire-form">
        <h2 className="ff-accent-heading font-display text-3xl uppercase mb-6">Enquire about this forklift</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="ff-label">Name*</label><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="ff-input" data-testid="enq-name" /></div>
          <div><label className="ff-label">Company</label><input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="ff-input" /></div>
          <div><label className="ff-label">Phone*</label><input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="ff-input" data-testid="enq-phone" /></div>
          <div><label className="ff-label">Email*</label><input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="ff-input" data-testid="enq-email" /></div>
          <div><label className="ff-label">Suburb</label><input value={form.suburb} onChange={e => setForm({ ...form, suburb: e.target.value })} className="ff-input" /></div>
          <div><label className="ff-label">I'm interested in</label>
            <select value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })} className="ff-input">
              {["Purchasing", "Inspection", "Trade-in", "Finance information", "Transport quote", "More information"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="ff-label">Message</label><textarea rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="ff-input" data-testid="enq-message" /></div>
        </div>
        <button disabled={sending} className="ff-btn-primary mt-6 disabled:opacity-60" data-testid="enq-submit">{sending ? "Sending…" : "Send Enquiry"}</button>
      </form>

      {lightboxOpen && (
        <Lightbox
          images={gallery}
          index={img}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setImg(i => (i - 1 + gallery.length) % gallery.length)}
          onNext={() => setImg(i => (i + 1) % gallery.length)}
        />
      )}
    </div>
  );
}
